import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [clients, setClients] = useState([]);
  const [activeClient, setActiveClient] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('wealthos_token');
    if (!token) { setLoading(false); return; }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data.user);
      setClients(res.data.clients);
      const savedClientId = localStorage.getItem('wealthos_client_id');
      const matched = res.data.clients.find(c => c.id === savedClientId);
      if (matched) {
        setActiveClient(matched);
      } else if (res.data.clients.length > 0) {
        setActiveClient(res.data.clients[0]);
        localStorage.setItem('wealthos_client_id', res.data.clients[0].id);
      }
    } catch {
      localStorage.removeItem('wealthos_token');
      localStorage.removeItem('wealthos_client_id');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('wealthos_token', res.data.token);
    setUser(res.data.user);
    setClients(res.data.clients);
    if (res.data.clients.length > 0) {
      setActiveClient(res.data.clients[0]);
      localStorage.setItem('wealthos_client_id', res.data.clients[0].id);
    }
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('wealthos_token');
    localStorage.removeItem('wealthos_client_id');
    setUser(null);
    setClients([]);
    setActiveClient(null);
  };

  const switchClient = (client) => {
    setActiveClient(client);
    localStorage.setItem('wealthos_client_id', client.id);
  };

  const refreshClients = async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data);
      // If active client was deleted, switch to first available
      if (activeClient && !res.data.find(c => c.id === activeClient.id)) {
        if (res.data.length > 0) {
          switchClient(res.data[0]);
        } else {
          setActiveClient(null);
          localStorage.removeItem('wealthos_client_id');
        }
      }
      return res.data;
    } catch (e) {
      console.error('Error refreshing clients', e);
      return clients;
    }
  };

  const addClient = async (clientData) => {
    const res = await api.post('/clients', clientData);
    const newClient = res.data;
    const updated = [...clients, newClient];
    setClients(updated);
    // Auto-switch to the new client
    switchClient(newClient);
    return newClient;
  };

  return (
    <AuthContext.Provider value={{
      user, clients, activeClient, loading,
      login, logout, switchClient, refreshClients, addClient
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
